import { Helmet } from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于酷客影院",
  description: "酷客影院品牌介绍、用户口碑与联系方式",
  url: "https://www.kukecinema.com/about",
  isPartOf: {
    "@type": "WebSite",
    name: "酷客影院",
    url: "https://www.kukecinema.com",
  },
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "酷客影院",
  url: "https://www.kukecinema.com",
  description: "酷客影院提供海量高清电影和高清电视剧在线观看服务，支持超清播放，画质清晰不缓冲。",
  email: "contact@kukecinema.com",
  telephone: "+86-400-888-9999",
  address: {
    "@type": "PostalAddress",
    streetAddress: "望京SOHO T1",
    addressLocality: "北京",
    addressRegion: "朝阳区",
    addressCountry: "CN",
  },
  sameAs: [
    "https://weibo.com/kukecinema",
    "https://space.bilibili.com/kukecinema",
  ],
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "酷客影院是否需要注册会员？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "目前酷客影院所有内容均可免费在线观看，无需注册会员即可畅享全部高清影视资源。我们致力于为广大用户提供便捷的观影体验。",
      },
    },
    {
      "@type": "Question",
      name: "酷客影院的画质如何？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "酷客影院支持1080P全高清和4K超清画质播放，配备智能CDN加速节点，确保播放流畅不缓冲。我们持续优化播放体验，让您享受影院级视听盛宴。",
      },
    },
    {
      "@type": "Question",
      name: "酷客影院的资源更新频率是怎样的？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "酷客影院每日更新最新热门影视资源，涵盖电影、电视剧、综艺、动漫等多种类型。热门大片上映后我们会第一时间上线高清版本，让您不再错过任何精彩内容。",
      },
    },
    {
      "@type": "Question",
      name: "酷客影院支持哪些设备观看？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "酷客影院全面支持电脑端（Windows/Mac）、手机端（iOS/Android）和平板设备在线观看。响应式网页设计确保在不同屏幕尺寸下都有良好的观影体验。",
      },
    },
    {
      "@type": "Question",
      name: "如何联系酷客影院的客服？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "您可以通过以下方式联系我们：发送邮件至 contact@kukecinema.com，或拨打客服热线 400-888-9999。我们的客服团队将在工作日9:00-18:00为您提供服务。",
      },
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>关于我们 - 酷客影院 | 品牌介绍与联系方式</title>
        <meta name="description" content="了解酷客影院的品牌故事、发展历程和核心优势。酷客影院致力于为全球华语用户提供高清电影和电视剧在线观看服务。查看用户口碑和联系方式。" />
        <meta name="keywords" content="酷客影院,关于我们,品牌介绍,联系方式,在线影院,高清影视" />
        <link rel="canonical" href="https://www.kukecinema.com/about" />
        <meta property="og:title" content="关于我们 - 酷客影院" />
        <meta property="og:description" content="了解酷客影院的品牌故事与核心优势，致力于提供高清影视在线观看服务。" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-screen-2xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: "关于我们" },
                ]}
              />
            </div>
          </div>

          <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20cinema%20interior%20with%20comfortable%20red%20seats%20and%20large%20blank%20screen%2C%20warm%20ambient%20lighting%2C%20clean%20and%20elegant%20design%2C%20cinematic%20atmosphere%2C%20professional%20photography&width=1600&height=700&seq=about-hero-01&orientation=landscape"
              alt="酷客影院 - 品牌形象"
              title="酷客影院品牌形象"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-black/30"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3">
                  关于酷客影院
                </h1>
                <p className="text-white/80 text-sm md:text-base max-w-md mx-auto">
                  用心做好每一帧，让观影成为一种享受
                </p>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-screen-2xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
                    <p>
                      <strong>酷客影院</strong>成立于2020年，是一家专注于提供<strong>高清电影</strong>和<strong>高清电视剧</strong>在线观看服务的影视平台。我们秉持"用心做好每一帧"的理念，致力于为全球华语用户打造极致的在线观影体验。
                    </p>
                    <p>
                      经过多年的技术积累与内容建设，酷客影院已经发展成为拥有数万部<strong>高清影视</strong>资源的综合性观影平台。我们与国内外多家内容提供商建立了深度合作关系，确保平台内容持续更新，覆盖电影、电视剧、综艺、动漫、纪录片等多种类型。
                    </p>
                    <p>
                      在技术方面，酷客影院自建智能CDN加速网络，支持<strong>1080P全高清</strong>和<strong>4K超清播放</strong>，画质清晰、播放流畅。我们持续优化播放体验，确保无论您在电脑、手机还是平板上观看，都能享受到丝滑流畅的观影体验。
                    </p>
                    <p>
                      未来，酷客影院将继续深耕<strong>高清在线观看</strong>领域，不断丰富内容库，提升技术实力，让每一位用户都能在这里找到自己喜欢的影视内容，享受影院级的视听盛宴。
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20home%20theater%20setup%20with%20large%20TV%20screen%20displaying%20colorful%20cinematic%20scene%2C%20cozy%20living%20room%20with%20warm%20lighting%2C%20comfortable%20atmosphere%2C%20clean%20minimal%20design%2C%20professional%20interior%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="酷客影院观影体验"
                    title="酷客影院 - 观影体验"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-screen-2xl mx-auto">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  用户口碑
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  来自真实用户的评价与反馈
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    name: "影迷小王",
                    rating: 5,
                    comment: "画质真的太棒了！4K超清播放完全不会缓冲，比很多收费平台体验还好。资源更新也很快，新片上线速度给力！",
                    date: "2024年7月",
                  },
                  {
                    name: "追剧达人",
                    rating: 5,
                    comment: "自从发现酷客影院之后就没换过平台了，资源真的全，想看的剧基本都能找到。手机端体验也很流畅，通勤路上追剧必备。",
                    date: "2024年7月",
                  },
                  {
                    name: "电影发烧友",
                    rating: 4,
                    comment: "分类做得很好，找片子很方便。画质清晰度没得说，就是希望纪录片资源能再多一些。整体体验非常满意！",
                    date: "2024年6月",
                  },
                  {
                    name: "周末宅家",
                    rating: 5,
                    comment: "周末在家用投影仪看酷客影院，效果堪比私人影院！1080P画质在大屏幕上也很清晰，推荐给所有电影爱好者。",
                    date: "2024年6月",
                  },
                  {
                    name: "学生党小李",
                    rating: 5,
                    comment: "免费的还能有这种画质，简直是学生党的福音！宿舍四个人一起看电影，氛围绝了。推荐给了好多同学。",
                    date: "2024年5月",
                  },
                  {
                    name: "剧荒拯救者",
                    rating: 5,
                    comment: "片库真的太丰富了！每次剧荒的时候来酷客逛逛，总能发现一些冷门好片。精选专题也做得很有品味。",
                    date: "2024年5月",
                  },
                ].map((review) => (
                  <div key={review.name} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < review.rating ? "ri-star-fill text-accent-500" : "ri-star-line text-background-300"}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                      "{review.comment}"
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-foreground-900">{review.name}</span>
                      <span className="text-foreground-400">{review.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-screen-2xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                    联系我们
                  </h2>
                  <p className="text-foreground-600 text-sm md:text-base mb-6">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们的客服团队将在工作日 9:00 - 18:00 为您提供服务。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground-900 text-sm">电子邮箱</h4>
                        <p className="text-foreground-500 text-sm">contact@kukecinema.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground-900 text-sm">客服热线</h4>
                        <p className="text-foreground-500 text-sm">400-888-9999</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground-900 text-sm">公司地址</h4>
                        <p className="text-foreground-500 text-sm">北京市朝阳区望京SOHO T1</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground-900 text-sm">工作时间</h4>
                        <p className="text-foreground-500 text-sm">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                    在线留言
                  </h2>
                  <form
                    id="about-contact-form"
                    data-readdy-form
                    action="https://readdy.ai/api/form/d9n4kcu8mbnljin59tm0"
                    method="POST"
                    className="space-y-4 p-5 rounded-lg bg-background-100 border border-background-200/70"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const formData = new FormData(form);
                      try {
                        const res = await fetch(form.action, {
                          method: "POST",
                          headers: { "Content-Type": "application/x-www-form-urlencoded" },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const responseText = await res.text();
                        let parsed;
                        try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                        if (res.ok && parsed?.code === "OK") {
                          alert("留言提交成功！感谢您的反馈。");
                          form.reset();
                        } else {
                          const errMsg = parsed?.meta?.message || parsed?.message || "提交失败，请稍后再试。";
                          alert(errMsg);
                        }
                      } catch {
                        alert("网络错误，请检查网络后重试。");
                      }
                    }}
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请选择或输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（不超过500字）"
                      ></textarea>
                    </div>
                    <div className="form-honeypot">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      提交留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-screen-2xl mx-auto">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  常见问题
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  快速了解酷客影院的常见问题
                </p>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  {
                    q: "酷客影院是否需要注册会员？",
                    a: "目前酷客影院所有内容均可免费在线观看，无需注册会员即可畅享全部高清影视资源。我们致力于为广大用户提供便捷的观影体验。",
                  },
                  {
                    q: "酷客影院的画质如何？",
                    a: "酷客影院支持1080P全高清和4K超清画质播放，配备智能CDN加速节点，确保播放流畅不缓冲。我们持续优化播放体验，让您享受影院级视听盛宴。",
                  },
                  {
                    q: "酷客影院的资源更新频率是怎样的？",
                    a: "酷客影院每日更新最新热门影视资源，涵盖电影、电视剧、综艺、动漫等多种类型。热门大片上映后我们会第一时间上线高清版本，让您不再错过任何精彩内容。",
                  },
                  {
                    q: "酷客影院支持哪些设备观看？",
                    a: "酷客影院全面支持电脑端（Windows/Mac）、手机端（iOS/Android）和平板设备在线观看。响应式网页设计确保在不同屏幕尺寸下都有良好的观影体验。",
                  },
                  {
                    q: "如何联系酷客影院的客服？",
                    a: "您可以通过以下方式联系我们：发送邮件至 contact@kukecinema.com，或拨打客服热线 400-888-9999。我们的客服团队将在工作日9:00-18:00为您提供服务。",
                  },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="font-medium text-foreground-900 text-sm pr-4">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}